import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../api/apiClient';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const { user } = useAuth();

  const [cartItems, setCartItems] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [voucherCodeState, setVoucherCodeState] = useState(() => {
    if (typeof window === 'undefined') return '';
    try {
      return window.localStorage.getItem('cartVoucherCode') || '';
    } catch (e) {
      return '';
    }
  });

  const setVoucherCode = useCallback((code) => {
    const normalized = code || '';
    setVoucherCodeState(normalized);
    if (typeof window !== 'undefined') {
      try {
        if (normalized) {
          window.localStorage.setItem('cartVoucherCode', normalized);
        } else {
          window.localStorage.removeItem('cartVoucherCode');
        }
      } catch (e) {
        // ignore storage errors
      }
    }
  }, []);

  const resetState = useCallback(() => {
    setCartItems([]);
    setCartCount(0);
    setVoucherCode('');
  }, [setVoucherCode]);

  const fetchCart = useCallback(async () => {
    if (!user?.id) {
      resetState();
      return;
    }

    setIsLoading(true);
    try {
      const response = await api.cart.getItems();
      const { items = [], count = 0 } = response.data?.data || {};
      setCartItems(items);
      setCartCount(count);
    } catch (error) {
      console.error('Failed to fetch cart items', error);
      toast.error('Unable to load your cart right now.');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, resetState]);

  const refreshCart = useCallback(() => {
    return fetchCart();
  }, [fetchCart]);

  const addCourseToCart = useCallback(async (courseId) => {
    if (!courseId) {
      toast.error('Unable to add course to cart.');
      return;
    }

    try {
      const response = await api.cart.add(courseId);
      toast.success(response.data?.message || 'Course added to cart');
      await fetchCart();
    } catch (error) {
      console.error('Failed to add course to cart', error);
      const errorMessage = error.response?.data?.message || 'Failed to add course to cart';
      toast.error(errorMessage);
      throw error;
    }
  }, [fetchCart]);

  const removeCourseFromCart = useCallback(async (courseId) => {
    if (!courseId) {
      toast.error('Unable to remove course.');
      return;
    }

    try {
      const response = await api.cart.remove(courseId);
      toast.success(response.data?.message || 'Course removed from cart');
      await fetchCart();
    } catch (error) {
      console.error('Failed to remove cart item', error);
      const errorMessage = error.response?.data?.message || 'Failed to remove course from cart';
      toast.error(errorMessage);
      throw error;
    }
  }, [fetchCart]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const value = useMemo(() => ({
    cartItems,
    cartCount,
    isLoading,
    refreshCart,
    addCourseToCart,
    removeCourseFromCart,
    voucherCode: voucherCodeState,
    setVoucherCode
  }), [
    cartItems,
    cartCount,
    isLoading,
    refreshCart,
    addCourseToCart,
    removeCourseFromCart,
    voucherCodeState,
    setVoucherCode
  ]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

